import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tabs, Card, Select, Space, Typography, Row, Col, Spin, Alert, Button, Tooltip } from 'antd';
import {
  DashboardOutlined,
  LineChartOutlined,
  BarChartOutlined,
  FundOutlined,
  SwapOutlined,
  ReloadOutlined,
  SendOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchDashboard,
  fetchAppStats,
  fetchConversions,
  fetchUserAnalytics,
  fetchPushPerformance,
  selectDashboard,
  selectAppStats,
  selectConversions,
  selectUserAnalytics,
  selectPushPerformance,
  selectLoadingAny,
  selectStatsError,
  clearStats
} from 'store/slices/stats.slice';
import type { StatsRequest } from 'store/slices/stats.slice';
import { fetchApplicationsLookup, selectApplicationsLookup } from 'store/slices/applications.slice';
import { DashboardOverview } from 'components/statistics/DashboardOverview';
import { AppStatsCard } from 'components/statistics/AppStatsCard';
import { PushPerformanceChart } from 'components/statistics/PushPerformanceChart';
import { ConversionStats } from 'components/statistics/ConversionStats';
import { UserAnalyticsChart } from 'components/statistics/UserAnalyticsChart';
import { TopCampaignsTable } from 'components/statistics/TopCampaignsTable';
import { ExportButton } from 'components/statistics/ExportButton';
import { CompareCampaigns } from 'components/statistics/CompareCampaigns';
import { CompareSegments } from 'components/statistics/CompareSegments';

import styles from './statistics.module.css';

const { Title, Text } = Typography;

type Period = '7d' | '30d' | '90d';

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
  { value: '90d', label: '90 дней' },
];

const FilterLabel: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4, minHeight: 17 }}>
    {children}
  </Text>
);

const TAB_REQUESTS: Record<string, StatsRequest[]> = {
  overview: ['dashboard', 'appStats', 'conversions'],
  push: ['pushPerformance'],
  conversions: ['conversions'],
  users: ['userAnalytics'],
  compare: [],
};

export const Statistics: React.FC = () => {
  const dispatch = useAppDispatch();

    const dashboard = useAppSelector(selectDashboard);
  const appStats = useAppSelector(selectAppStats);
  const conversions = useAppSelector(selectConversions);
  const userAnalytics = useAppSelector(selectUserAnalytics);
  const pushPerformance = useAppSelector(selectPushPerformance);
  const error = useAppSelector(selectStatsError);
  const applications = useAppSelector(selectApplicationsLookup);

  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('30d');
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [appsLoaded, setAppsLoaded] = useState(false);

  const tabLoading = useAppSelector(selectLoadingAny(TAB_REQUESTS[activeTab] ?? []));
  const appOptions = useMemo(
    () => (applications ?? []).map((app) => ({ value: app.id, label: app.name })),
    [applications],
  );

  const overviewMetrics = useMemo(() => {
    const users = appStats?.users;
    const push = appStats?.push_stats;

    return [
      {
        key: 'total_users',
        title: 'Пользователи',
        value: users?.total_users ?? 0,
        icon: <BarChartOutlined />,
        tone: styles.info,
      },
      {
        key: 'active_users',
        title: 'Активные',
        value: users?.active_users ?? 0,
        icon: <TrophyOutlined />,
        tone: styles.success,
      },
      {
        key: 'sent',
        title: 'Отправлено',
        value: push?.total_sent ?? 0,
        icon: <SendOutlined />,
        tone: '',
      },
      {
        key: 'delivered',
        title: 'Доставлено',
        value: push?.total_delivered ?? 0,
        icon: <CheckCircleOutlined />,
        tone: '',
      },
      {
        key: 'ctr',
        title: 'Средний CTR',
        value: `${(push?.avg_ctr ?? 0).toFixed(2)}%`,
        icon: <BarChartOutlined />,
        tone: styles.warning,
      },
      {
        key: 'push_conversions',
        title: 'Конверсии из push',
        value: (push?.attributed_registrations ?? 0) + (push?.attributed_deposits ?? 0),
        icon: <TrophyOutlined />,
        tone: styles.danger,
      },
    ];
  }, [appStats]);

  const loadTabData = useCallback(
    (tab: string, appId: number, period: Period) => {
      switch (tab) {
        case 'overview':
          return Promise.all([
            dispatch(fetchDashboard(appId)),
            dispatch(fetchAppStats({ appId, period })),
            dispatch(fetchConversions({ appId, period })),
          ]);
        case 'conversions':
          return dispatch(fetchConversions({ appId, period }));
        case 'users':
          return dispatch(fetchUserAnalytics({ appId, period, groupBy: 'day' }));
        case 'push':
          return dispatch(fetchPushPerformance({ appId, period }));
        default:
          return Promise.resolve();
      }
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(fetchApplicationsLookup()).finally(() => setAppsLoaded(true));
  }, [dispatch]);

  useEffect(() => {
    if (applications && applications.length > 0 && selectedAppId === null) {
      setSelectedAppId(applications[0].id);
    }
  }, [applications, selectedAppId]);

  useEffect(() => {
    if (!selectedAppId) return;
    loadTabData(activeTab, selectedAppId, selectedPeriod);
  }, [selectedAppId, selectedPeriod, activeTab, loadTabData]);

  useEffect(() => () => {
    dispatch(clearStats());
  }, [dispatch]);

  const handleRefresh = async () => {
    if (!selectedAppId) return;

    setRefreshing(true);
    try {
      await loadTabData(activeTab, selectedAppId, selectedPeriod);
    } finally {
      setRefreshing(false);
    }
  };

  if (!appsLoaded) {
    return (
      <div className={styles.statsContainer} style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">Загрузка приложений...</Text>
        </div>
      </div>
    );
  }

  if (!applications || applications.length === 0) {
    return (
      <div className={styles.statsContainer}>
        <Alert
          type="warning"
          message="Приложения не найдены"
          description="Создайте приложение, чтобы увидеть статистику."
          showIcon
          className={styles.modernAlert}
        />
      </div>
    );
  }

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <DashboardOutlined /> Обзор
        </span>
      ),
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {appStats && (
            <Row gutter={[12, 12]}>
              {overviewMetrics.map((metric) => (
                <Col key={metric.key} xs={12} sm={8} lg={4}>
                  <AppStatsCard
                    title={metric.title}
                    value={metric.value}
                    icon={metric.icon}
                    className={metric.tone}
                    compact
                  />
                </Col>
              ))}
            </Row>
          )}

          <DashboardOverview
            pushStats={appStats?.push_stats}
            conversions={conversions?.conversions}
          />

          {dashboard?.top_campaigns && dashboard.top_campaigns.length > 0 && (
            <Card
              title={
                <span className={styles.chartTitle}>
                  <div className={styles.chartTitleIcon}>
                    <FundOutlined />
                  </div>
                  Топ кампаний
                </span>
              }
              className={styles.statsCard}
            >
              <TopCampaignsTable campaigns={dashboard.top_campaigns} />
            </Card>
          )}
        </Space>
      ),
    },
    {
      key: 'push',
      label: (
        <span>
          <LineChartOutlined /> Push
        </span>
      ),
      children: pushPerformance ? <PushPerformanceChart data={pushPerformance} /> : null,
    },
    {
      key: 'conversions',
      label: (
        <span>
          <FundOutlined /> Конверсии
        </span>
      ),
      children: conversions ? <ConversionStats data={conversions} /> : null,
    },
    {
      key: 'users',
      label: (
        <span>
          <BarChartOutlined /> Пользователи
        </span>
      ),
      children: userAnalytics ? <UserAnalyticsChart data={userAnalytics} /> : null,
    },
    {
      key: 'compare',
      label: (
        <span>
          <SwapOutlined /> Сравнение
        </span>
      ),
      children: selectedAppId ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <CompareCampaigns appId={selectedAppId} />
          <CompareSegments appId={selectedAppId} />
        </Space>
      ) : null,
    },
  ];

  return (
    <div className={styles.statsContainer}>
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ marginBottom: 4, fontSize: 20 }}>
          Аналитика и статистика
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Push-уведомления и конверсии по приложению
        </Text>
      </div>

      <Card
        className={styles.statsCard}
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: '12px 16px' } }}
      >
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} sm={12} md={8} lg={6}>
            <FilterLabel>Приложение</FilterLabel>
            <Select
              showSearch
              value={selectedAppId}
              onChange={setSelectedAppId}
              options={appOptions}
              optionFilterProp="label"
              filterSort={(a, b) => (a.label ?? '').localeCompare(b.label ?? '')}
              notFoundContent="Ничего не найдено"
              placeholder="Начните вводить название"
              style={{ width: '100%' }}
              size="small"
              className={styles.modernSelect}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <FilterLabel>Период</FilterLabel>
            <Select
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              options={PERIOD_OPTIONS}
              style={{ width: '100%' }}
              size="small"
              className={styles.modernSelect}
            />
          </Col>

          <Col xs={12} sm={8} md={4} lg={3}>
            <FilterLabel />
            <Tooltip title="Обновить данные">
              <Button
                onClick={handleRefresh}
                loading={refreshing}
                size="small"
                block
                icon={<ReloadOutlined />}
              >
                Обновить
              </Button>
            </Tooltip>
          </Col>

          {selectedAppId && (
            <Col xs={12} sm={8} md={6} lg={3}>
              <FilterLabel />
              <ExportButton appId={selectedAppId} period={selectedPeriod} />
            </Col>
          )}
        </Row>
      </Card>

      {error && (
        <Alert
          type="error"
          message="Ошибка загрузки"
          description={error}
          closable
          style={{ marginBottom: 16 }}
          className={styles.modernAlert}
        />
      )}

      <Spin spinning={tabLoading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size="small" className={styles.modernTabs} />
      </Spin>
    </div>
  );
};

export default Statistics;